import React from "react";
import { Box, Button, Chip, Sheet, Stack, Typography } from "@mui/joy";
import { OrganizationIntegration } from "../../common/types";
import { IntegrationCatalogEntry } from "../../types/integrationCatalog";
import IntegrationCardFrame from "./IntegrationCardFrame";
import { hasSubstrateFields } from "./integrationConfigUtils";

interface TierACardProps {
  entry: IntegrationCatalogEntry;
  integrations: OrganizationIntegration[];
  enabled: boolean;
  saving?: boolean;
  preview?: string | null;
  canEditCredentials?: boolean;
  rowPreview?: (integration: OrganizationIntegration) => string | null;
  onEnable: () => void;
  onManageSubstrate: (integration: OrganizationIntegration) => void;
  onEditCredentials: (integration: OrganizationIntegration) => void;
  onDisable: (integration: OrganizationIntegration) => void;
}

const rowStatusColor = (integration: OrganizationIntegration) => {
  if (integration.status === "active") return "success";
  if (integration.status === "inactive") return "neutral";
  return "warning";
};

const rowStatusLabel = (integration: OrganizationIntegration) => {
  if (integration.status === "active") return "Configured";
  if (integration.status === "inactive") return "Disabled";
  return "Not enabled";
};

const TierACard: React.FC<TierACardProps> = ({
  entry,
  integrations,
  enabled,
  saving = false,
  preview,
  canEditCredentials = true,
  rowPreview,
  onEnable,
  onManageSubstrate,
  onEditCredentials,
  onDisable,
}) => {
  const hasRows = integrations.length > 0;
  const isMultiRow = integrations.length > 1;
  const singleIntegration =
    !isMultiRow && hasRows ? integrations[0] : undefined;
  const showManageSubstrate = hasSubstrateFields(entry);

  const renderRowActions = (integration: OrganizationIntegration) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      {showManageSubstrate && (
        <Button
          size="sm"
          variant="outlined"
          color="primary"
          onClick={() => onManageSubstrate(integration)}
          disabled={saving}
        >
          Manage substrate
        </Button>
      )}
      {canEditCredentials && (
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => onEditCredentials(integration)}
          disabled={saving}
        >
          Edit credentials
        </Button>
      )}
      <Button
        size="sm"
        variant="outlined"
        color="danger"
        onClick={() => onDisable(integration)}
        disabled={saving}
      >
        Disable
      </Button>
    </Stack>
  );

  const cardLevelActions =
    hasRows && singleIntegration ? (
      renderRowActions(singleIntegration)
    ) : !hasRows ? (
      <Button size="sm" variant="solid" onClick={onEnable} disabled={saving}>
        Enable
      </Button>
    ) : null;

  const multiRowMetadata = isMultiRow ? (
    <Stack spacing={1.25} sx={{ mt: 0.5 }}>
      {integrations.map((integration) => {
        const rowSubtitle = rowPreview?.(integration);
        return (
          <Sheet
            key={integration.id}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: "sm",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.25,
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  level="title-sm"
                  title={integration.name}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {integration.name}
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={rowStatusColor(integration) as any}
                >
                  {rowStatusLabel(integration)}
                </Chip>
              </Stack>
              {rowSubtitle && (
                <Typography level="body-xs" color="neutral">
                  {rowSubtitle}
                </Typography>
              )}
            </Box>
            {renderRowActions(integration)}
          </Sheet>
        );
      })}
    </Stack>
  ) : undefined;

  return (
    <IntegrationCardFrame
      entry={entry}
      integration={singleIntegration}
      enabled={enabled}
      statusLabel={
        enabled
          ? isMultiRow
            ? `${integrations.filter((i) => i.status === "active").length} of ${integrations.length} active`
            : "Configured"
          : singleIntegration?.status === "inactive"
            ? "Disabled"
            : "Not enabled"
      }
      tierBadge="Org + Workspace configuration"
      preview={isMultiRow ? null : preview}
      metadata={multiRowMetadata}
      actions={cardLevelActions}
    />
  );
};

export default TierACard;
